import {
  GatsbyImage,
  IGatsbyImageData,
  StaticImage,
} from "gatsby-plugin-image";
import React, { ReactNode, useState } from "react";
import renderContentfulRichText from "../../helpers/render-contentful-rich-text";
import Button from "../button/Button";

type ImagesType =
  | readonly ({
      readonly gatsbyImageData: IGatsbyImageData | null;
      readonly title: string | null;
      readonly description: string | null;
    } | null)[]
  | null;

type ImageCarouselProps = {
  images: readonly ({
    readonly gatsbyImageData: IGatsbyImageData | null;
    readonly title: string | null;
    readonly description: string | null;
  } | null)[];
  activeImage: number;
};

type VideoType = {
  readonly file: {
    readonly contentType: string | null;
    readonly fileName: string | null;
    readonly url: string | null;
  } | null;
};

type PartnersType =
  | readonly ({
      readonly title: string | null;
      readonly logotype: {
        readonly gatsbyImageData: IGatsbyImageData | null;
      } | null;
      readonly url: string | null;
    } | null)[]
  | null;

const Page = ({
  children,
  noMargin = false,
  droppedParent = true
}: {
  children?: ReactNode[] | ReactNode | string;
  droppedParent?: boolean
  noMargin?: boolean;
}) => {
  return (
    <div className="relative">
      {droppedParent && <div className="bg-c-hero h-24 w-full absolute"></div>}
      <div className="relative container mx-auto lg:px-36">
        <div
          className={`md:drop-shadow-md bg-white ${
            !noMargin && `mb-16 md:mb-48`
          }`}
        >
          {children}
        </div>
      </div>
    </div>
  );
};

export const PageImage = ({ images }: { images: ImagesType }) => {
  if (!images || !images[0]) return <></>;
  return (
    <div className="w-full overflow-hidden bg-slate-500">
      {images[0].gatsbyImageData != null && (
        <div>
          <GatsbyImage
            alt={images[0].title ?? "Bild"}
            image={images[0].gatsbyImageData}
          />
        </div>
      )}
    </div>
  );
};

export const PageBody = ({
  children,
}: {
  children: { raw: string | null };
}) => {
  return (
    <div className="p-10 lg:p-24">
      <div className="rich-text">{renderContentfulRichText(children)}</div>
    </div>
  );
};

export const PageBodyPlain = ({ children }: { children: ReactNode }) => {
  return <div className="p-10 lg:p-24">{children}</div>;
};

export const PageLocation = ({
  link,
  title,
}: {
  link: string;
  title: string;
}) => {
  return (
    <div className="mb-24">
      <Button kind="anchor" appearance="hollow" to={link}>
        {title}
      </Button>
    </div>
  );
};

export const PageGallery = ({
  images,
  bigTitle,
  smallTitle,
}: {
  images: ImagesType;
  bigTitle: string;
  smallTitle: string;
}) => {
  const [activeImage, setActiveImage] = useState<number>(0);

  if (!images || !images[0]) return <></>;

  const changeActiveImage = (direction: "forward" | "backward") => {
    if (images) {
      if (direction === "forward") {
        if (activeImage < images.length - 1) {
          setActiveImage(activeImage + 1);
        } else {
          setActiveImage(0);
        }
      } else if (direction === "backward") {
        if (activeImage > 0) {
          setActiveImage(activeImage - 1);
        } else {
          setActiveImage(images.length - 1);
        }
      }
    }
  };

  const ImageCarousel = (props: ImageCarouselProps) => {
    let { images, activeImage } = props;
    const image = images[activeImage];

    if (image === null) return <></>;
    if (!image.gatsbyImageData) return <></>;

    return (
      <GatsbyImage
        alt={image.title ? image.title : "Image"}
        image={image.gatsbyImageData}
      />
    );
  };

  const getDescription = () => {
    return images[activeImage]?.description ?? "";
  };

  return (
    <div className="bg-c-gallery-bg pt-24 pb-24 flex flex-col items-center">
      <h2 className="text-c-gallery-subtitle italic text-3xl uppercase font-header">
        {smallTitle}
      </h2>
      <h3 className="text-white text-5xl md:text-6xl uppercase px-5 lg:w-2/4 font-header">
        {bigTitle}
      </h3>
      <div className="flex items-center gap-x-5 mt-5">
        {images.length > 1 && (
          <div onClick={() => changeActiveImage("backward")}>
            <StaticImage
              src="../../images/small-arrow-left.png"
              alt="Arrow left"
              height={134}
            />
          </div>
        )}
        <div>
          <div className="w-[270px] max-h-[200px] sm:w-[400px] sm:max-h-[400px] xl:w-[800px] xl:max-h-[800px] overflow-hidden">
            <ImageCarousel activeImage={activeImage} images={images} />
          </div>
        </div>
        {images.length > 1 && (
          <div onClick={() => changeActiveImage("forward")}>
            <StaticImage
              src="../../images/small-arrow-right.png"
              className="cursor-pointer"
              alt="Arrow Right"
              height={134}
            />
          </div>
        )}
      </div>
      <div className="text-white text-base mt-4 italic whitespace-pre-line">
        {getDescription()}
      </div>
    </div>
  );
};

export const PageVideo = ({
  video,
  title,
}: {
  video: VideoType;
  title: string;
}) => {
  return (
    <div className="pt-10 pb-10 md:pt-24 md:pb-24 flex flex-col items-center">
      <h2 className="text-c-video-title text-4xl font-header uppercase w-2/4">
        {title}
      </h2>
      <div className="w-[350px] max-h-[200px] md:w-[500px] md:max-h-[350px] overflow-hidden mt-5">
        <video controls>
          {video.file !== null &&
            video.file.url !== null &&
            video.file.contentType !== null && (
              <source src={video.file.url} type={video.file.contentType} />
            )}
        </video>
      </div>
    </div>
  );
};

export const PagePartners = ({ partners }: { partners: PartnersType }) => {
  return (
    <div className="pt-24 pb-24 flex flex-col items-center">
      <h2 className="text-4xl font-header uppercase w-2/4">Partners</h2>
      <ul className="flex gap-x-5 mt-5">
        {partners?.map((partner, i) => {
          if (!partner) return;
          if (!partner.logotype) return;
          if (!partner.logotype.gatsbyImageData) return;
          return (
            <li key={i}>
              {partner.url ? (
                <a href={partner.url} target="_blank" rel="no-follow">
                  <GatsbyImage
                    alt={partner.title ? partner.title : "Partner"}
                    title={partner.title ? partner.title : "Partner"}
                    image={partner.logotype.gatsbyImageData}
                  />
                </a>
              ) : (
                <GatsbyImage
                  alt={partner.title ? partner.title : "Partner"}
                  title={partner.title ? partner.title : "Partner"}
                  image={partner.logotype.gatsbyImageData}
                />
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default Page;
